import { DropdownMenuLabel } from '@heidi/design-system/dropdown-menu';
import { TypographyP2, typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { formatCjkAwareDisplayName, getCjkAwareDisplayInitials } from '@heidi/utils/i18n';
import { useIntl } from 'react-intl';

import {
  AssigneeMemberItem,
  AssigneeTeamItem,
} from './clinical-task-assignee-list-items';

interface AssigneeMemberSearchResult {
  // Callers filter to a real, selectable member before passing this list down
  // (`kinde_user_id` is what a member resolves to) — never re-filter here.
  kinde_user_id: string;
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  profile_picture_url?: string | null;
}

interface AssigneeTeamOption {
  id: string;
  name: string;
}

interface ClinicalTaskAssigneeResultsListProps {
  hasQuery: boolean;
  isResultsPending: boolean;
  showNoResults: boolean;
  enableTeams: boolean;
  filteredTeams: AssigneeTeamOption[];
  members: AssigneeMemberSearchResult[];
  assigneeId?: string | null;
  assignedLabel: string;
  onSelectTeam: (team: AssigneeTeamOption) => void;
  onSelectMember: (
    kindeUserId: string,
    name: string | null,
    avatarUrl: string | null
  ) => void;
}

// Search-result states (loading / no-results / team matches / member matches)
// for the assignee dropdown, split out so the selector's own file stays under
// the line-count ceiling.
export function ClinicalTaskAssigneeResultsList({
  hasQuery,
  isResultsPending,
  showNoResults,
  enableTeams,
  filteredTeams,
  members,
  assigneeId,
  assignedLabel,
  onSelectTeam,
  onSelectMember,
}: ClinicalTaskAssigneeResultsListProps) {
  const intl = useIntl();
  const showHeaders = enableTeams && hasQuery && !isResultsPending;
  const headerClassName = cn(
    typographyVariant({ variant: 'caption' }),
    'pt-1 text-text-secondary'
  );

  return (
    <>
      {isResultsPending && (
        <div className="p-2">
          <TypographyP2 className="text-text-tertiary">
            {intl.formatMessage({ defaultMessage: 'Loading…', id: 'T4VxQNSBvZ' })}
          </TypographyP2>
        </div>
      )}

      {showHeaders && filteredTeams.length > 0 && (
        <DropdownMenuLabel className={headerClassName}>
          {intl.formatMessage({ defaultMessage: 'Teams', id: 'DNeuzb5ky2' })}
        </DropdownMenuLabel>
      )}

      {enableTeams &&
        hasQuery &&
        !isResultsPending &&
        filteredTeams.length > 0 &&
        filteredTeams.map((team) => (
          <AssigneeTeamItem
            key={team.id}
            id={team.id}
            name={team.name}
            isSelected={team.id === assigneeId}
            onSelect={() => onSelectTeam(team)}
          />
        ))}

      {showNoResults && (
        <div className="p-2">
          <TypographyP2 className="text-text-tertiary">
            {intl.formatMessage({
              defaultMessage: 'No results found',
              id: 'hX5PAbCTOR',
            })}
          </TypographyP2>
        </div>
      )}

      {showHeaders && members.length > 0 && (
        <DropdownMenuLabel className={headerClassName}>
          {intl.formatMessage({ defaultMessage: 'Members', id: '+a+2ugZ5sB' })}
        </DropdownMenuLabel>
      )}

      {hasQuery &&
        !isResultsPending &&
        members.map((member) => {
          const kindeUserId = member.kinde_user_id;
          const name = formatCjkAwareDisplayName(
            { firstName: member.first_name, lastName: member.last_name },
            intl.locale
          );
          const label = name || member.email || assignedLabel;

          return (
            <AssigneeMemberItem
              key={kindeUserId}
              label={label}
              colorSeed={name || member.email || ''}
              avatarUrl={member.profile_picture_url}
              initials={
                getCjkAwareDisplayInitials(
                  { firstName: member.first_name, lastName: member.last_name },
                  intl.locale
                ) || '?'
              }
              isSelected={kindeUserId === assigneeId}
              onSelect={() =>
                onSelectMember(
                  kindeUserId,
                  name || member.email || null,
                  member.profile_picture_url ?? null
                )
              }
            />
          );
        })}
    </>
  );
}
